import { useState, KeyboardEvent, ClipboardEvent, ChangeEvent } from "react";
import { EmailTagInputProps, EmailTag } from "./EmailTagInput.types";

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function useEmailTagInput({ value, onChange, maxEmails = 20, disabled }: EmailTagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email.trim());
  };

  const isDuplicate = (email: string): boolean => {
    return value.some((existingEmail) => existingEmail.toLowerCase() === email.toLowerCase());
  };

  const parseEmailsFromText = (text: string): string[] => {
    // Split by comma, semicolon, space, newline, tab
    return text
      .split(/[,;\s\n\t]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) return;
    if (value.length >= maxEmails) return;
    if (isDuplicate(trimmedEmail)) return; // Automatski odbaci duplikat
    if (!validateEmail(trimmedEmail)) return; // Samo validni emailovi se dodaju

    onChange([...value, trimmedEmail]);
    setInputValue("");
  };

  const addMultipleEmails = (emails: string[]) => {
    const validEmails: string[] = [];

    for (const email of emails) {
      const trimmedEmail = email.trim();
      
      // Proveri da li smo dostigli limit
      if (value.length + validEmails.length >= maxEmails) break;
      
      // Proveri duplikat u postojećim i novim emailovima
      if (isDuplicate(trimmedEmail)) continue;
      if (validEmails.some((e) => e.toLowerCase() === trimmedEmail.toLowerCase())) continue;
      
      // Dodaj samo validne emailove
      if (validateEmail(trimmedEmail)) {
        validEmails.push(trimmedEmail);
      }
    }

    if (validEmails.length > 0) {
      onChange([...value, ...validEmails]);
    }
    setInputValue("");
  };

  const removeEmail = (index: number) => {
    const newEmails = [...value];
    newEmails.splice(index, 1);
    onChange(newEmails);
  };

  const removeLastEmail = () => {
    if (value.length > 0) {
      const newEmails = [...value];
      newEmails.pop();
      onChange(newEmails);
    }
  };

  const clearAll = () => {
    onChange([]);
    setInputValue("");
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    // Proveri da li je unet delimiter (,;)
    const delimiterMatch = newValue.match(/[,;]/);
    if (delimiterMatch) {
      const emails = parseEmailsFromText(newValue);
      if (emails.length > 0) {
        addMultipleEmails(emails);
      } else {
        setInputValue("");
      }
    } else {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Enter - dodaj email
    if (event.key === "Enter") {
      event.preventDefault();
      if (inputValue.trim()) {
        addEmail(inputValue);
      }
      return;
    }

    // Backspace - ako je input prazan, obriši poslednji tag
    if (event.key === "Backspace" && !inputValue) {
      event.preventDefault();
      removeLastEmail();
      return;
    }

    // Space - dodaj email
    if (event.key === " ") {
      event.preventDefault();
      if (inputValue.trim()) {
        addEmail(inputValue);
      }
      return;
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emails = parseEmailsFromText(pastedText);
    
    if (emails.length > 0) {
      addMultipleEmails(emails);
    }
  };

  const getEmailTags = (): EmailTag[] => {
    return value.map((email) => ({
      email,
      isValid: validateEmail(email),
    }));
  };

  return {
    inputValue,
    emailTags: getEmailTags(),
    handleInputChange,
    handleKeyDown,
    handlePaste,
    removeEmail,
    clearAll,
    canAddMore: value.length < maxEmails,
    isAtLimit: value.length >= maxEmails,
  };
}
